import { useCallback, useEffect } from 'react'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import { useRecoilValue, useSetRecoilState } from 'recoil'
import { getQueryPredictions, QueryResult, searchForPlaces } from 'src/utils/google-places'
import { AddressResult, AMSearchRequest } from 'src/common-services'
import { SearchOption } from 'src/v2/components/sidebar-search/search-input'
import { useMapExplorerNavigation } from 'src/views/map-explorer/sidebar/search/use-navigation'
import { useMapExplorerChoice } from 'src/views/map-explorer/sidebar/search/use-choice'
import isEqual from 'lodash/isEqual'
import { Bounds } from 'src/types/map'
import { browserLogger } from 'src/utils/browser-logger'

import * as atoms from 'src/v2/state/map-explorer/search'
import * as selectorAtoms from 'src/v2/state/map-explorer/selector'
import * as mainMapAtoms from 'src/atoms/main-map'

export const useGetGooglePlacesByQuery = (bounds?: Bounds) => {
  const mapBounds = useRecoilValue(mainMapAtoms.bounds)

  const getGooglePlacesByQuery = useCallback(
    async (query: string) => {
      if (query === '') {
        return undefined
      }

      // Try finding places based on the query
      let googlePlaces = await searchForPlaces({
        bounds: bounds ?? mapBounds,
        searchText: query,
      })

      // If no places found, try searching for suggestions and then search for places
      if (!googlePlaces || !googlePlaces.length) {
        const suggestions = await getQueryPredictions({
          bounds: mapBounds,
          input: query,
        })
        const suggestionsPlaces = suggestions.map((suggestion) =>
          searchForPlaces({
            bounds: mapBounds,
            searchText: suggestion.description,
          })
        )
        googlePlaces = (await Promise.all(suggestionsPlaces)).flat()
      }

      return googlePlaces
    },
    [mapBounds, bounds]
  )

  return {
    getGooglePlacesByQuery,
  }
}

export type SearchOptions = {
  bounds?: AMSearchRequest['bounds']
  resultTypes?: AMSearchRequest['resultTypes']
  // we can add other options here like searchTypes?: nearby | by-parcel
}
export const useSearch = (options?: SearchOptions) => {
  const setMapBounds = useSetRecoilState(mainMapAtoms.bounds)
  const [currentResultTypes, setResultTypes] = useAtom(atoms.resultTypes)
  const setAddressesResults = useSetAtom(atoms.addressesResults)
  const { getGooglePlacesByQuery } = useGetGooglePlacesByQuery(options?.bounds)
  const { clearSelected } = useMapExplorerNavigation()
  const { clearChoice } = useMapExplorerChoice()
  const inEntitySelector = Boolean(useAtomValue(selectorAtoms.supportedSelections))

  const search = async (query: string, moveMap?: boolean) => {
    browserLogger.info('Initiating search', { query })
    inEntitySelector ? clearChoice() : await clearSelected()
    const googlePlaces = await getGooglePlacesByQuery(query)
    if (!googlePlaces || !googlePlaces.length) {
      setAddressesResults(undefined)
      return
    }
    if (moveMap) {
      const bounds = googlePlaces[0].geometry.viewport
      setMapBounds(bounds)
    }
    const results: AddressResult[] = googlePlaces.map((place) => ({
      id: place.place_id,
      type: 'address',
      title: place.name,
      subtitle: place.formatted_address,
      meta: {
        point: {
          type: 'Point',
          coordinates: place.geometry.location,
        },
      },
    }))
    setAddressesResults(results)
  }

  const clear = async () => {
    setAddressesResults(undefined)
    inEntitySelector ? clearChoice() : await clearSelected()
  }

  useEffect(() => {
    if (options?.resultTypes && !isEqual(options.resultTypes, currentResultTypes)) {
      browserLogger.info('Setting result types', { old: currentResultTypes, new: options.resultTypes })
      setResultTypes(options.resultTypes)
    }
  }, [options, currentResultTypes])

  return {
    search,
    clear,
  }
}

const googleSuggestionsToMapExplorerSearchOptions = (
  suggestions: QueryResult[]
): Exclude<SearchOption, undefined>[] => {
  return suggestions.map((place) => {
    if ('types' in place && place.types?.includes('political')) {
      return {
        id: 'political_:' + place.description,
        title: place.description,
        subtitle: 'Moves map to ' + place.description,
        value: place.description,
      }
    }
    if ('place_id' in place) {
      return {
        id: 'place_id:' + place.place_id,
        title: place.structured_formatting.main_text,
        subtitle: place.structured_formatting.secondary_text,
        value: place.description,
      }
    }
    return {
      id: 'search:' + place.description,
      title: place.structured_formatting.main_text,
      subtitle: place.structured_formatting.secondary_text,
      value: place.description,
    }
  })
}
export const useSearchSuggestions = () => {
  const mapBounds = useRecoilValue(mainMapAtoms.bounds)

  const getSearchSuggestions = async (query: string) => {
    const googleSuggestions = await getQueryPredictions({
      bounds: mapBounds,
      input: query,
    })

    return googleSuggestionsToMapExplorerSearchOptions(googleSuggestions)
  }
  return {
    getSearchSuggestions,
  }
}
